/**
 * The primitive type vocabulary shared by every package.
 *
 * These live in `sim` rather than `content` because `content` imports sim types
 * (TRD-02 R-02-01) and the reverse would be a cycle.
 */

/** Monotonic within a run, never reused (TRD-01 R-01-09). */
export type EntityId = number;

export const NO_TARGET = -1 as const;

/** TRD-02 R-02-05 — kebab-case, stable forever. Renaming one invalidates stored replays. */
export type TowerId = string;
export type EnemyId = string;
export type MapId = string;
export type BiomeId = string;
export type MutatorId = string;
export type UnlockNodeId = string;

export const CLASS_IDS = [
  'warden',
  'marksman',
  'arcanist',
  'venom',
  'frost',
  'tempest',
  'alchemist',
  'beacon',
  'swarm',
  'void',
] as const;

/**
 * Declaration order is load-bearing: synergy effects are applied in this order during
 * recompute, and `add` does not commute with `mul` (TRD-08 R-08-08).
 */
export type ClassId = (typeof CLASS_IDS)[number];

export type DamageKind = 'physical' | 'magic' | 'true';

export type StatusKind = 'poison' | 'chill' | 'shock' | 'wither' | 'mark' | 'stun' | 'freeze';

export type TargetingMode = 'first' | 'last' | 'closest' | 'strongest' | 'weakest';

/**
 * TRD-05 §5. `beam`, `laser` and `chain` resolve on the fire tick and never travel.
 *
 * `beam` is a one-shot hitscan — a rifle. `laser` is the sustained variant: a low
 * damage-per-tick channel that ramps the longer it stays on one enemy, which is a
 * different weapon entirely even though both are "instant hitscan" in the code.
 *
 * `zone` (B-18/B-19) neither travels nor resolves instantly: the cast anchors a timed
 * AREA at the target's position — telegraph, then repeated strikes — stepped by the
 * zone system rather than the projectile pool.
 */
export type ProjectileKind = 'bullet' | 'homing' | 'beam' | 'laser' | 'lob' | 'chain' | 'zone';

/** Kinds that resolve on the fire tick rather than spawning a travelling projectile. */
export type HitscanKind = Extract<ProjectileKind, 'beam' | 'laser' | 'chain'>;

export function isHitscan(kind: ProjectileKind): kind is HitscanKind {
  return kind === 'beam' || kind === 'laser' || kind === 'chain';
}

export type StarLevel = 1 | 2 | 3;

export type SynergyTier = 2 | 4 | 6;

export type Phase = 'build' | 'combat' | 'ended';

export type GoldReason = 'waveClear' | 'interest' | 'purchase' | 'sell' | 'reroll' | 'drop';

export type SpeedMultiplier = 1 | 2 | 4;
